/* TheQuartermaster v2 — Stateside Quartermaster storefront shopping assistant.
   Rule-based, uses only public storefront endpoints (no private keys).
   Features: persistent chat across pages, item-vs-detail search with re-ranking, budget/branch filters,
   price/stock/sale tags, add-to-cart in chat, page-aware help, custom/bulk routing,
   military shorthand, cart reminder, compare, New Arrivals + hand-picked Featured Gear collections. */
(() => {
  'use strict';
  const root = document.querySelector('[data-quartermaster]');
  if (!root || root.dataset.qmV2) return;
  root.dataset.qmV2 = '1';

  // ---------- config / elements ----------
  let ctx = {};
  try { ctx = JSON.parse(root.querySelector('[data-qm-context]')?.textContent || '{}'); } catch (_) {}
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const R = {
    cart: ctx.cartUrl || '/cart', checkout: '/checkout', search: ctx.searchUrl || '/search',
    contact: ctx.contactUrl || '/pages/contact', account: ctx.accountUrl || '/account',
    custom: ctx.customUrl || '/pages/custom-unit-gear', customForm: (ctx.customUrl || '/pages/custom-unit-gear') + '#custom-inquiry',
    customProducts: ctx.customCollection || '/collections/custom-personalized-products',
    upload: ctx.uploadUrl || '', rfq: ctx.rfqUrl || '/pages/request-for-quote',
    newCol: ctx.newCollection || 'thequartermaster-new-arrivals', featCol: ctx.featuredCollection || 'thequartermaster-featured-gear',
    shipping: '/policies/shipping-policy', refund: '/policies/refund-policy'
  };
  const $ = (s) => root.querySelector(s);
  const openBtn = $('[data-qm-open]'), panel = $('[data-qm-panel]'), closeBtn = $('[data-qm-close]'),
    messages = $('[data-qm-messages]'), quick = $('[data-qm-quick]'), form = $('[data-qm-form]'),
    input = $('[data-qm-input]'), send = $('[data-qm-send]'), status = $('[data-qm-launch-status]'),
    countBadge = $('[data-qm-count]'), clearBtn = $('[data-qm-clear]');
  if (!openBtn || !panel || !messages || !form || !input) return;

  // ---------- persistent state ----------
  const KEY = 'qm-v2-state';
  const state = { log: [], open: false, branch: '', budget: null, last: [], lastQuery: '', busy: false, remindedKey: '' };
  const load = () => {
    try {
      const s = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (s && Array.isArray(s.log)) Object.assign(state, { log: s.log.slice(-40), open: !!s.open, branch: s.branch || '', budget: s.budget || null, last: s.last || [], lastQuery: s.lastQuery || '', remindedKey: s.remindedKey || '' });
    } catch (_) {}
  };
  const save = () => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ log: state.log.slice(-40), open: state.open, branch: state.branch, budget: state.budget, last: state.last.slice(0, 8), lastQuery: state.lastQuery, remindedKey: state.remindedKey }));
    } catch (_) {}
  };

  // ---------- helpers ----------
  const money = (cents) => {
    const n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + (n / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const toCents = (v) => { const n = parseFloat(String(v).replace(/[^0-9.]/g, '')); return isFinite(n) ? Math.round(n * 100) : null; };
  const imgUrl = (src, w = 160) => {
    if (!src) return '';
    let s = typeof src === 'string' ? src : (src.url || src.src || '');
    if (!s) return '';
    if (s.startsWith('//')) s = 'https:' + s;
    try { const u = new URL(s, location.origin); u.searchParams.set('width', String(w)); return u.href; } catch (_) { return s; }
  };
  const esc = (t) => String(t == null ? '' : t);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const getJSON = async (url, opts = {}, timeout = 9000) => {
    const ctrl = new AbortController(); const t = setTimeout(() => ctrl.abort(), timeout);
    try {
      let r;
      for (let attempt = 0; attempt < 3; attempt++) {
        r = await fetch(url, Object.assign({ headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: ctrl.signal }, opts));
        if (r.status !== 429 || (opts.method && opts.method !== 'GET')) break;
        await sleep(900 * (attempt + 1));
      }
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return await r.json();
    } finally { clearTimeout(t); }
  };
  const searchUrl = (q) => `${R.search}?q=${encodeURIComponent(q)}&type=product`;

  // ---------- rendering (all entries are data, so they can be restored after navigation) ----------
  const scroll = () => { messages.scrollTop = messages.scrollHeight; };
  const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };

  function renderEntry(en) {
    if (en.k === 'msg') {
      const d = el('div', `qm-msg qm-msg--${en.role}`);
      if (en.title) d.append(el('b', '', en.title));
      if (en.text) en.text.split('\n').forEach((line) => d.append(el('p', '', line)));
      if (en.links && en.links.length) {
        const w = el('div', 'qm-links');
        en.links.forEach((x) => { const a = el('a', x.primary ? 'qm-link--primary' : '', x.label); a.href = x.href; if (x.ext) { a.target = '_blank'; a.rel = 'noopener'; } w.append(a); });
        d.append(w);
      }
      if (en.chips && en.chips.length) {
        const w = el('div', 'qm-choices');
        en.chips.forEach((x) => { const b = el('button', '', x.label); b.type = 'button'; if (x.q) b.dataset.query = x.q; if (x.action) b.dataset.act = x.action; if (x.arg != null) b.dataset.arg = String(x.arg); w.append(b); });
        d.append(w);
      }
      return d;
    }
    if (en.k === 'products') {
      const w = el('div', 'qm-products');
      en.items.forEach((p) => w.append(productCard(p)));
      return w;
    }
    if (en.k === 'cart') {
      const d = el('div', 'qm-msg qm-msg--assistant qm-cartbox');
      d.append(el('b', '', en.title || 'Your cart'));
      const list = el('div', 'qm-cartlist');
      en.items.forEach((i) => {
        const row = el('a', 'qm-cartrow'); row.href = i.url || R.cart;
        if (i.img) { const im = el('img'); im.src = imgUrl(i.img, 96); im.alt = ''; im.loading = 'lazy'; row.append(im); } else row.append(el('span', 'qm-img', 'ITEM'));
        const c = el('span', 'qm-cartcopy'); c.append(el('span', 'qm-product-title', i.title)); c.append(el('span', 'qm-product-meta', `Qty ${i.qty} · ${money(i.line)}`)); row.append(c); list.append(row);
      });
      d.append(list);
      if (en.more) d.append(el('p', 'qm-small', `+ ${en.more} more item${en.more === 1 ? '' : 's'}`));
      d.append(el('p', 'qm-subtotal', `Subtotal: ${money(en.subtotal)}`));
      const w = el('div', 'qm-links');
      const a1 = el('a', 'qm-link--primary', 'Checkout'); a1.href = R.checkout; const a2 = el('a', '', 'View cart'); a2.href = R.cart; w.append(a1, a2); d.append(w);
      d.append(el('p', 'qm-small', 'Shipping and taxes are calculated at checkout.'));
      return d;
    }
    return null;
  }
  function productCard(p) {
    const card = el('div', 'qm-product' + (p.available ? '' : ' qm-product--out'));
    const link = el('a', 'qm-product-link'); link.href = p.url;
    if (p.img) { const i = el('img'); i.src = imgUrl(p.img, 160); i.alt = ''; i.loading = 'lazy'; link.append(i); } else link.append(el('span', 'qm-img', 'ITEM'));
    const c = el('span', 'qm-product-copy');
    c.append(el('span', 'qm-product-title', p.title));
    const pr = el('span', 'qm-price');
    if (p.price != null) {
      const now = el('span', p.compare > p.price ? 'qm-price-now qm-price-sale' : 'qm-price-now', (p.priceVaries ? 'From ' : '') + money(p.price));
      pr.append(now);
      if (p.compare > p.price) pr.append(el('s', 'qm-price-was', money(p.compare)));
    }
    c.append(pr);
    const tags = el('span', 'qm-tags');
    tags.append(el('span', 'qm-tag ' + (p.available ? 'qm-tag--in' : 'qm-tag--out'), p.available ? 'In stock' : 'Sold out'));
    if (p.compare > p.price) tags.append(el('span', 'qm-tag qm-tag--sale', 'Sale'));
    if (p.vendor) tags.append(el('span', 'qm-tag qm-tag--muted', p.vendor));
    c.append(tags);
    link.append(c);
    card.append(link);
    const act = el('div', 'qm-product-actions');
    if (p.available && p.single && p.variantId) {
      const b = el('button', 'qm-add', 'Add to cart'); b.type = 'button'; b.dataset.qmAdd = String(p.variantId); b.dataset.title = p.title; act.append(b);
    } else if (p.available) {
      const a = el('a', 'qm-add qm-add--alt', 'Choose options'); a.href = p.url; act.append(a);
    } else {
      const a = el('a', 'qm-add qm-add--muted', 'View item'); a.href = p.url; act.append(a);
    }
    card.append(act);
    return card;
  }
  function push(en, persist = true) {
    const node = renderEntry(en); if (!node) return null;
    messages.append(node); scroll();
    if (persist) { state.log.push(en); if (state.log.length > 40) state.log = state.log.slice(-40); save(); }
    return node;
  }
  const say = (text, title = '', extra = {}) => push(Object.assign({ k: 'msg', role: 'assistant', text, title }, extra));
  const you = (text) => push({ k: 'msg', role: 'user', text });
  const statusMsg = (text) => { const d = el('div', 'qm-msg qm-msg--status'); d.append(el('span', 'qm-dots', '')); d.append(el('span', '', ' ' + text)); messages.append(d); scroll(); return d; };

  // ---------- panel open/close ----------
  function show(v, focus = true) {
    panel.hidden = !v; openBtn.setAttribute('aria-expanded', String(v)); state.open = v; save();
    if (v) { scroll(); if (focus) setTimeout(() => input.focus(), 30); onOpen(); } else if (focus) openBtn.focus();
  }

  // ---------- language understanding ----------
  const BRANCHES = [
    [/\bjrotc\b/i, 'JROTC'], [/\brotc\b/i, 'ROTC'], [/\bair (national )?guard\b|\bang\b/i, 'Air National Guard'],
    [/\bstate (guard|defense force)\b|\bsdf\b/i, 'State Guard'], [/\bnational guard\b|\barmy guard\b|\barng\b/i, 'National Guard'],
    [/\bspace force\b|\bussf\b/i, 'Space Force'], [/\bcoast guard\b|\buscg\b/i, 'Coast Guard'], [/\bmarines?\b|\bmarine corps\b|\busmc\b/i, 'Marine Corps'],
    [/\bair force\b|\busaf\b/i, 'Air Force'], [/\bnavy\b|\busn\b/i, 'Navy'], [/\barmy\b/i, 'Army']
  ];
  const detectBranch = (t) => { for (const [re, name] of BRANCHES) if (re.test(t)) return name; return ''; };
  const SYN = [
    [/\bocps?\b|\bscorpion w2\b/gi, 'OCP'], [/\bmulti ?cam\b/gi, 'Multicam'], [/\bcamoflage|camoflague|camouflauge\b/gi, 'camouflage'],
    [/\btq'?s?\b|\btourniquets?\b/gi, 'tourniquet'], [/\bifak'?s?\b/gi, 'IFAK'], [/\bfirst[- ]?aid\b/gi, 'first aid'],
    [/\bnods\b|\bnvgs?\b|\bnight ?vision goggles?\b/gi, 'night vision'], [/\bpc\b(?=.*\b(armor|plate|carrier)\b)|\bplate ?carriers?\b/gi, 'plate carrier'],
    [/\bbullet ?proof\b/gi, 'ballistic'], [/\bbody ?armou?r\b/gi, 'body armor'], [/\bpt (shirt|shorts|gear|uniform)s?\b/gi, 'PT $1'],
    [/\bdog ?tags?\b/gi, 'dog tag'], [/\bname ?tapes?\b|\bnametapes?\b/gi, 'name tape'], [/\bchallenge coins?\b/gi, 'challenge coin'],
    [/\bzipp?o'?s?\b|\bzipo\b/gi, 'Zippo'], [/\bka-?bar\b/gi, 'KA-BAR'], [/\bgaurd\b/gi, 'guard'], [/\bmill?itary\b/gi, 'military'],
    [/\bsargent|seargent|sergant\b/gi, 'sergeant'], [/\bleiutenant|lieutenent|leutenant\b/gi, 'lieutenant'], [/\bboonie( hat)?s?\b/gi, 'boonie hat'],
    [/\bhydration (pack|bladder)s?\b|\bcamelbak\b/gi, 'hydration'], [/\bmolle\b/gi, 'MOLLE'], [/\bk-?9\b/gi, 'K9'], [/\bdog gear\b/gi, 'K9'],
    [/\bscuba gear\b/gi, 'dive scuba'], [/\bfield manuals?\b|\bfm ?\d+[-\d]*\b/gi, 'field manual'], [/\bgas ?masks?\b/gi, 'gas mask'],
    [/\bflash ?lights?\b/gi, 'flashlight'], [/\bhead ?lamps?\b/gi, 'headlamp'], [/\bshadow ?box(es)?\b/gi, 'shadow box'], [/\bflag ?cases?\b/gi, 'flag case']
  ];
  const FILLER = /\b(please|can you|could you|would you|will you|help me|i need|i want|i'm looking for|im looking for|looking for|show me|find me|find|get me|do you (have|sell|carry)|you guys|any|some|a few|what about|what do you have for|for me|thanks|thank you)\b/gi;
  const BUDGET = /(?:under|below|less than|cheaper than|max(?:imum)?(?: of)?|up to|budget(?: is| of)?|no more than|<)\s*\$?\s*(\d{1,6}(?:\.\d{1,2})?)|\$\s*(\d{1,6})(?:\s*(?:or less|max|and under|budget))/i;
  function remember(t) {
    const b = detectBranch(t); if (b) state.branch = b;
    const m = t.match(BUDGET); if (m) { const v = Number(m[1] || m[2]); if (v > 0) state.budget = v; }
    if (/\b(no budget|any price|price doesn'?t matter|clear budget)\b/i.test(t)) state.budget = null;
    save();
  }
  function normalize(t) {
    let q = ' ' + t + ' ';
    q = q.replace(BUDGET, ' ');
    SYN.forEach(([re, rep]) => { q = q.replace(re, rep); });
    q = q.replace(FILLER, ' ').replace(/[?!.,]+/g, ' ').replace(/\b(for|with|the|a|an|my|our|in|of|and|to|that|is|are|on)\b/gi, ' ').replace(/\s+/g, ' ').trim();
    return (q.length > 1 ? q : t.trim()).slice(0, 120);
  }

  // ---------- catalog ----------
  const detailCache = new Map();
  async function productDetails(handle) {
    if (!handle) return null;
    if (detailCache.has(handle)) return detailCache.get(handle);
    const p = (async () => {
      try {
        const d = await getJSON(`${base}products/${encodeURIComponent(handle)}.js`, {}, 8000);
        const vs = Array.isArray(d.variants) ? d.variants : [];
        const avail = vs.filter((v) => v.available);
        const pick = avail[0] || vs[0] || {};
        const prices = vs.map((v) => v.price).filter((n) => typeof n === 'number');
        return {
          id: d.id, handle: d.handle, title: d.title, url: `${base}products/${d.handle}`, img: d.featured_image || (d.images && d.images[0]) || '',
          price: typeof d.price_min === 'number' ? d.price_min : (pick.price ?? null), priceMax: d.price_max, priceVaries: !!d.price_varies,
          compare: (pick.compare_at_price && pick.compare_at_price > (pick.price || 0)) ? pick.compare_at_price : 0,
          available: !!d.available, single: vs.length === 1, variantId: (vs.length === 1 ? pick.id : null),
          vendor: d.vendor || '', type: d.type || '', options: (d.options || []).map((o) => (typeof o === 'string' ? o : o.name)).filter((n) => n && n !== 'Title'),
          variants: vs.slice(0, 60).map((v) => ({ title: v.title, available: !!v.available, price: v.price })), tags: (d.tags || []).slice(0, 40), minPrice: prices.length ? Math.min(...prices) : null
        };
      } catch (_) { detailCache.delete(handle); return null; }
    })();
    detailCache.set(handle, p);
    return p;
  }
  const slim = (p) => ({ id: p.id, handle: p.handle, title: p.title, url: p.url, img: p.img, price: p.price, priceVaries: p.priceVaries, compare: p.compare, available: p.available, single: p.single, variantId: p.variantId, vendor: p.vendor, type: p.type, options: p.options });
  const FIELDS_FOCUSED = 'title,product_type,tag';
  const FIELDS_WIDE = 'title,product_type,tag,vendor,variants.sku,variants.title';
  async function suggest(q, limit = 10, fields = FIELDS_FOCUSED) {
    const p = new URLSearchParams();
    p.set('q', q); p.set('resources[type]', 'product,collection'); p.set('resources[limit]', String(limit));
    p.set('resources[options][unavailable_products]', 'last'); p.set('resources[options][fields]', fields);
    const d = await getJSON(`${base}search/suggest.json?${p}`);
    const res = (d && d.resources && d.resources.results) || {};
    return { products: Array.isArray(res.products) ? res.products : [], collections: Array.isArray(res.collections) ? res.collections : [] };
  }
  function rerank(items, q, quals = []) {
    const toks = q.toLowerCase().split(/\s+/).filter((w) => w.length > 1);
    const qt = quals.join(' ').toLowerCase().split(/\s+/).filter((w) => w.length > 1);
    const stem = (w) => w.replace(/(es|s)$/, '');
    const score = (p) => {
      const t = (p.title || '').toLowerCase(), ty = (p.type || '').toLowerCase(), tg = (Array.isArray(p.tags) ? p.tags.join(' ') : '').toLowerCase();
      let s = 0, coreHits = 0;
      toks.forEach((w) => { const w2 = stem(w); if (t.includes(w2)) { s += 6; coreHits++; } else if (ty.includes(w2)) { s += 5; coreHits++; } else if (tg.includes(w2)) { s += 2; coreHits++; } });
      if (toks.length && coreHits === 0) s -= 12;
      if (toks.length && toks.every((w) => t.includes(stem(w)))) s += 4;
      if (toks.length && toks.every((w) => ty.includes(stem(w)))) s += 5;
      qt.forEach((w) => { if (t.includes(w) || tg.includes(w) || ty.includes(w)) s += 2; });
      if (p.available) s += 1;
      if (/\b(paperback|hardcover|board book)\b/i.test(p.title || '') && !/\b(book|manual|guide|reading|literature|history)\b/i.test(q)) s -= 6;
      return s;
    };
    return items.map((p, i) => ({ p, s: score(p), i })).sort((a, b) => b.s - a.s || a.i - b.i).filter((x, i, arr) => x.s > -6 || arr.every((y) => y.s <= -6)).map((x) => x.p);
  }
  const QUAL_RE = /\b(u\.?s\.?|army|navy|air force|usaf|marines?|marine corps|usmc|coast guard|uscg|space force|ussf|national guard|air national guard|state guard|guard|rotc|jrotc|ocp|multicam|scorpion|coyote( brown)?|tan|black|white|red|blue|navy blue|od green|olive( drab)?|ranger green|green|gray|grey|woodland|desert|camo|camouflage|men'?s|mens|women'?s|womens|ladies|kids?|youth|unisex|x?x?s(mall)?|small|medium|large|x+-?large|xl|xxl|[2-5]xl|size \S+)\b/gi;
  function splitQuery(q) {
    const quals = (q.match(QUAL_RE) || []).map((x) => x.trim());
    const core = q.replace(QUAL_RE, ' ').replace(/\s+/g, ' ').trim();
    return { core: core.length >= 3 ? core : '', quals: core.length >= 3 ? quals : [] };
  }
  async function detailsFor(handles, n = 8) {
    const list = await Promise.all(handles.slice(0, n).map((h) => productDetails(h)));
    return list.filter(Boolean);
  }

  // ---------- flows ----------
  async function searchFlow(raw) {
    const q = normalize(raw);
    if (!q) return say('Give me an item, category, branch, brand, or SKU and I’ll search the current catalog.');
    state.busy = true; send.disabled = true; state.lastQuery = q; save();
    const st = statusMsg(`Checking current supply for “${q}”…`);
    try {
      const { core, quals } = splitQuery(q);
      const branchQual = state.branch && !detectBranch(q) ? state.branch : '';
      const allQuals = quals.concat(branchQual ? [branchQual] : []);
      let found = { products: [], collections: [] };
      const merge = (extra) => {
        const seen = new Set(found.products.map((p) => p.handle));
        found.products = found.products.concat(extra.products.filter((p) => !seen.has(p.handle)));
        if (!found.collections.length) found.collections = extra.collections;
      };
      found = await suggest(core || q);
      if (core && allQuals.length) { try { merge(await suggest(`${allQuals.join(' ')} ${core}`)); } catch (_) {} }
      if (found.products.length < 4 || /\d/.test(q)) { try { merge(await suggest(core || q, 10, FIELDS_WIDE)); } catch (_) {} }
      const ranked = rerank(found.products, core || q, allQuals);
      const usedBranch = branchQual && ranked.slice(0, 3).some((p) => new RegExp(branchQual.split(' ')[0], 'i').test(`${p.title} ${(p.tags || []).join(' ')}`)) ? branchQual : '';
      let items = await detailsFor(ranked.map((p) => p.handle), 7);
      st.remove();
      if (!items.length) {
        if (found.collections.length) {
          return say(`No direct product match for “${q}”, but these departments look related.`, 'Closest matches', { links: found.collections.slice(0, 5).map((c) => ({ label: c.title, href: c.url })) });
        }
        return say(`I didn’t find a strong match for “${q}”. Try a shorter term, a brand, or a SKU — or ask the store team.`, 'No match yet', { links: [{ label: 'Search the full store', href: searchUrl(q) }, { label: 'Ask the store team', href: R.contact }], chips: customHint(raw) ? [{ label: 'This is a custom order', action: 'custom' }] : [] });
      }
      let note = '';
      if (state.budget) {
        const cap = Math.round(state.budget * 100);
        const within = items.filter((p) => (p.minPrice ?? p.price ?? Infinity) <= cap);
        if (within.length) { items = within; note = ` Everything shown is $${state.budget.toFixed(0)} or less.`; }
        else { items = items.slice().sort((a, b) => (a.minPrice ?? a.price) - (b.minPrice ?? b.price)).slice(0, 3); note = ` Nothing matched at $${state.budget.toFixed(0)} or less — here are the lowest-priced options I found.`; }
      }
      items.sort((a, b) => Number(b.available) - Number(a.available));
      const seenT = new Set(); items = items.filter((p) => { const k = p.title.trim().toLowerCase(); if (seenT.has(k)) return false; seenT.add(k); return true; });
      const shown = items.slice(0, 6);
      state.last = shown.map(slim); save();
      const br = usedBranch ? ` Filtered for ${usedBranch}.` : '';
      say(`Here’s what I found for “${q}”.${br}${note}`, 'Supply check complete');
      push({ k: 'products', items: shown.map(slim) });
      const chips = [];
      if (!state.budget) chips.push({ label: 'Under $50', q: `${q} under $50` }, { label: 'Under $100', q: `${q} under $100` });
      else chips.push({ label: 'Any price', action: 'clearbudget', arg: q });
      if (state.branch && !usedBranch) chips.push({ label: `Only ${state.branch}`, q: `${state.branch} ${q}` });
      if (shown.length >= 2) chips.push({ label: 'Compare top 2', action: 'compare' });
      if (customHint(raw)) chips.push({ label: 'Make it custom', action: 'custom' });
      say('Want me to narrow it down? Tell me a size, color or camo, brand, or budget.', '', { chips, links: [{ label: 'See all results', href: searchUrl(q) }] });
    } catch (e) {
      st.remove();
      say('The live catalog lookup is busy right now. You can use the full store search, or I can connect you with the team.', 'Temporary hiccup', { links: [{ label: 'Search the store', href: searchUrl(q) }, { label: 'Contact the store', href: R.contact }] });
    } finally { state.busy = false; send.disabled = false; scroll(); }
  }

  async function collectionFlow(handle, title, lead) {
    state.busy = true; send.disabled = true;
    const st = statusMsg(`Pulling ${title.toLowerCase()}…`);
    try {
      const d = await getJSON(`${base}collections/${handle}/products.json?limit=12`);
      const handles = (d.products || []).map((p) => p.handle);
      const items = (await detailsFor(handles, 8)).filter((p) => p.available).slice(0, 6);
      st.remove();
      if (!items.length) throw new Error('empty');
      state.last = items.map(slim); save();
      say(lead, title);
      push({ k: 'products', items: items.map(slim) });
      say('Want these narrowed by branch, category, or budget?', '', { links: [{ label: `View all ${title.toLowerCase()}`, href: `${base}collections/${handle}` }] });
    } catch (_) {
      st.remove();
      say(`I couldn’t load ${title.toLowerCase()} just now.`, '', { links: [{ label: `Open ${title.toLowerCase()}`, href: `${base}collections/${handle}` }] });
    } finally { state.busy = false; send.disabled = false; }
  }

  async function getCart() {
    const c = await getJSON(`${base}cart.js`, { cache: 'no-store' }, 7000);
    updateLauncher(c.item_count || 0);
    return c;
  }
  function updateLauncher(n) {
    if (status) status.textContent = n ? `${n} item${n === 1 ? '' : 's'} in your cart` : 'Shopping assistant';
    if (countBadge) { countBadge.textContent = n > 99 ? '99+' : String(n); countBadge.hidden = !n; }
  }
  function syncHeaderCart(n) {
    try { sessionStorage.setItem('cart-count', JSON.stringify({ value: String(n), timestamp: Date.now() })); } catch (_) {}
    document.querySelectorAll('cart-icon').forEach((ci) => { try { if (typeof ci.renderCartBubble === 'function') ci.renderCartBubble(n); } catch (_) {} });
  }
  async function cartFlow(proactive = false) {
    try {
      const c = await getCart();
      if (!c.item_count) { if (!proactive) say('Your cart is empty right now. Tell me what you need and I’ll help you find it.', 'Cart check'); return; }
      const key = `${c.token || 'cart'}:${c.item_count}:${c.total_price || 0}`;
      if (proactive && state.remindedKey === key) return;
      state.remindedKey = key; save();
      const items = (c.items || []).slice(0, 4).map((i) => ({ title: i.product_title || i.title, qty: i.quantity, line: i.final_line_price, img: i.image, url: i.url }));
      push({ k: 'cart', title: proactive ? `You have ${c.item_count} item${c.item_count === 1 ? '' : 's'} waiting` : 'Your cart', items, more: Math.max(0, (c.items || []).length - 4), subtotal: c.total_price });
    } catch (_) { if (!proactive) say('I couldn’t read your cart just now.', 'Cart check', { links: [{ label: 'Open cart', href: R.cart }] }); }
  }

  async function addToCart(btn) {
    const id = Number(btn.dataset.qmAdd); if (!id) return;
    btn.disabled = true; const orig = btn.textContent; btn.textContent = 'Adding…';
    try {
      const r = await fetch(`${base}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.description || data.message || 'Could not add');
      btn.textContent = 'Added ✓'; btn.classList.add('qm-add--done');
      const c = await getCart(); syncHeaderCart(c.item_count || 0);
      say(`Added “${btn.dataset.title || 'item'}” to your cart. You now have ${c.item_count} item${c.item_count === 1 ? '' : 's'}.`, 'Added to cart', { links: [{ label: 'Checkout', href: R.checkout, primary: true }, { label: 'View cart', href: R.cart }] });
      setTimeout(() => { btn.textContent = orig; btn.disabled = false; btn.classList.remove('qm-add--done'); }, 2500);
    } catch (e) {
      btn.textContent = orig; btn.disabled = false;
      say(`That item couldn’t be added: ${e.message}. It may be out of stock or limited — open the product page for details.`, 'Cart', {});
    }
  }

  async function compareFlow() {
    if (state.last.length < 2) return say('Search for a category first, then I can compare the top results side by side.');
    const [a, b] = await Promise.all([productDetails(state.last[0].handle), productDetails(state.last[1].handle)]);
    if (!a || !b) return say('I couldn’t load both products to compare. Open them directly for full details.');
    const row = (label, f) => `${label}: ${f(a)}  |  ${f(b)}`;
    const opt = (p) => (p.options.length ? `${p.options.join(', ')} (${p.variants.filter((v) => v.available).length}/${p.variants.length} in stock)` : 'One option');
    const text = [
      `1) ${a.title}`, `2) ${b.title}`, '',
      row('Price', (p) => (p.priceVaries ? `${money(p.price)}–${money(p.priceMax)}` : money(p.price))),
      row('Availability', (p) => (p.available ? 'In stock' : 'Sold out')),
      row('Brand', (p) => p.vendor || '—'),
      row('Category', (p) => p.type || '—'),
      row('Options', opt),
      '', 'These are the facts from the listings. Check each product page for materials, dimensions, ratings, and certifications.'
    ].join('\n');
    say(text, 'Side-by-side', { links: [{ label: 'Open #1', href: a.url }, { label: 'Open #2', href: b.url }] });
  }

  function customHint(t) {
    return /\b(custom|customi[sz]ed?|personali[sz]ed?|embroider(ed|y)?|engrav(e|ed|ing)|print(ed)? (our|my)|with (my|our) (name|unit|logo|design)|(my|our) (unit|company|battalion|squadron|platoon|troop|department|team|school|logo|crest|design|artwork)|unit (logo|patch|shirt|coin|t-?shirt|flag)|guidons?|challenge coins?|name ?tapes?|nameplates?|dog ?tags?|made to order)\b/i.test(t);
  }
  function customFlow(t) {
    const links = [{ label: 'Start a custom order', href: R.customForm, primary: true }, { label: 'Browse custom products', href: R.customProducts }];
    if (R.upload) links.push({ label: 'Upload your artwork', href: R.upload, ext: true });
    say('That sounds like a custom job — our Custom Unit Gear team builds patches, name tapes, dog tags, T-shirts and apparel, flags and guidons, displays, and special-order gear to your spec.\nSend the project details on the Custom Unit Gear form (quantity, needed-by date, colors, sizes), and upload your logo or artwork if you have it. Vector files (AI, EPS, PDF, SVG) work best.', 'Custom Unit Gear', { links });
  }
  const bulkHint = (t) => /\b(bulk|wholesale|quote|rfq|agency|department order|unit order|large order|purchase order|\bpo\b|gsa|net ?30|tax[- ]exempt|volume (pricing|discount)|(\d{2,})\s*(pcs|pieces|units|each|ea|qty|of them|shirts|patches|items))\b/i.test(t);
  function bulkFlow() {
    say('For unit, agency, or bulk orders, send a Request for Quote with the items, quantities, and deadline. The team will reply with pricing, lead times, and any tax-exempt or purchase-order details — I won’t guess at volume pricing here.', 'Bulk & agency orders', { links: [{ label: 'Request a quote', href: R.rfq, primary: true }, { label: 'Contact the store', href: R.contact }] });
  }
  function orderFlow() {
    say('For order status, tracking, changes, or returns, check your account first. If the team needs to look into it, send your order number and the email you used at checkout.', 'Order help', { links: [{ label: 'My account', href: R.account, primary: true }, { label: 'Contact the store', href: R.contact }, { label: 'Refund policy', href: R.refund }] });
  }
  function policyFlow(t) {
    const apo = /\b(apo|fpo|dpo|overseas|deployed|military address)\b/i.test(t);
    say(`${apo ? 'For APO/FPO/DPO and overseas military addresses, availability and carrier options depend on the item. ' : ''}Shipping cost, delivery time, and taxes depend on the item and destination, and they’re shown at checkout. I won’t quote a rate or promise a delivery date here.`, 'Shipping & returns', { links: [{ label: 'Shipping policy', href: R.shipping }, { label: 'Refund policy', href: R.refund }, { label: 'Ask the team', href: R.contact }] });
  }
  function branchMenu() {
    say('Pick a branch or program and I’ll keep it in mind for your searches.', 'Shop by branch', { chips: ['Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force', 'National Guard', 'Air National Guard', 'State Guard', 'ROTC', 'JROTC'].map((b) => ({ label: b, action: 'setbranch', arg: b })) });
  }

  // ---------- page awareness ----------
  async function productHereFlow(kind) {
    const p = ctx.product; if (!p) return;
    if (kind === 'stock') {
      const d = await productDetails(p.handle); if (!d) return say('I couldn’t load this item’s details. The options on the page are current.');
      if (d.variants.length <= 1) return say(`${d.title} is ${d.available ? 'in stock' : 'currently sold out'} at ${money(d.price)}.`, 'This item');
      const inS = d.variants.filter((v) => v.available).map((v) => v.title), out = d.variants.filter((v) => !v.available).map((v) => v.title);
      return say(`${inS.length} of ${d.variants.length} options are in stock.${inS.length ? `\nIn stock: ${inS.slice(0, 20).join(', ')}${inS.length > 20 ? '…' : ''}` : ''}${out.length ? `\nSold out: ${out.slice(0, 12).join(', ')}${out.length > 12 ? '…' : ''}` : ''}`, 'Options & stock');
    }
    state.busy = true; send.disabled = true;
    const st = statusMsg('Finding items that pair well with this one…');
    try {
      let handles = [];
      for (const intent of ['complementary', 'related']) {
        try { const d = await getJSON(`${base}recommendations/products.json?product_id=${p.id}&limit=10&intent=${intent}`); handles = (d.products || []).map((x) => x.handle).filter((h) => h !== p.handle); } catch (_) {}
        if (handles.length >= 3) break;
      }
      if (handles.length < 3 && p.type) { const s = await suggest(p.type, 10); handles = handles.concat(s.products.map((x) => x.handle).filter((h) => h !== p.handle && !handles.includes(h))); }
      const items = (await detailsFor(handles, 8)).filter((x) => x.available).slice(0, 6);
      st.remove();
      if (!items.length) return say('I didn’t find strong matches for this item yet. Tell me what you’re pairing it with and I’ll search.');
      state.last = items.map(slim); save();
      say(`Here are items shoppers often look at alongside ${p.title}.`, 'You might also need');
      push({ k: 'products', items: items.map(slim) });
    } catch (_) { st.remove(); say('Recommendations are unavailable right now.'); }
    finally { state.busy = false; send.disabled = false; }
  }
  function contextChips() {
    if (ctx.product) return [{ label: 'Sizes & stock for this item', action: 'here-stock' }, { label: 'What goes with this?', action: 'here-pair' }];
    if (ctx.collection) return [{ label: `Picks from ${ctx.collection.title}`, action: 'here-col' }];
    return [];
  }

  // ---------- intent router ----------
  async function handle(raw, echo = true) {
    const t = String(raw || '').trim(); if (!t || state.busy) return;
    if (echo) you(t);
    remember(t);
    if (/^(hi|hello|hey|yo|good (morning|afternoon|evening)|howdy|sup)[!. ]*$/i.test(t)) return say('Good to have you. What’s the mission — what are you trying to find, replace, or put together?', '', { chips: [{ label: 'Find gear', action: 'find' }, { label: 'Custom unit gear', action: 'custom' }, { label: 'Featured gear', action: 'featured' }, { label: 'New arrivals', action: 'new' }] });
    if (/\b(are you (a )?(real|human|person|robot|bot|ai)|who are you|what are you)\b/i.test(t)) return say('I’m TheQuartermaster, Stateside Quartermaster’s virtual shopping assistant. I search the live catalog, check stock and prices, add items to your cart, and hand anything I can’t verify to the store team.');
    if (/\b(talk to|speak (to|with)|real person|human|representative|agent|call you|phone number|email you)\b/i.test(t)) return say('You can reach the Stateside Quartermaster team directly through the contact page. Include your order number if it’s about an existing order.', 'Talk to the team', { links: [{ label: 'Contact the store', href: R.contact, primary: true }] });
    if (/\b(my cart|in my cart|what'?s in (my )?cart|cart check|checkout)\b/i.test(t)) return cartFlow(false);
    if (/\b(order status|track(ing)?|where is my (order|package)|my order|shipment status|cancel my order|change my order|return (my|an|this)|refund|exchange)\b/i.test(t)) return orderFlow();
    if (/\b(shipping (cost|rate|time|policy)|how much is shipping|free shipping|deliver(y|ed) (time|date)|how long (to|does) (ship|shipping|delivery)|apo|fpo|dpo|ship (to|overseas)|international shipping|return policy)\b/i.test(t)) return policyFlow(t);
    if (bulkHint(t)) { bulkFlow(); if (!customHint(t)) return; }
    if (customHint(t)) { customFlow(t); if (!/\b(show|browse|see|find|what)\b/i.test(t)) return; }
    if (/\b(compare|comparison|difference between|which (one|is better)|vs\.?|versus)\b/i.test(t)) return compareFlow();
    if (/\b(shop by branch|branches|which branch)\b/i.test(t)) return branchMenu();
    if (/\b(new arrivals?|what'?s new|newest|latest)\b/i.test(t)) return collectionFlow(R.newCol, 'New arrivals', 'The newest listings in the store, newest first.');
    if (/\b(best ?sellers?|top sellers?|popular|hot items?|trending|most popular|featured|recommend(ed|ations?)?|what'?s good|staff picks?)\b/i.test(t)) return collectionFlow(R.featCol, 'Featured gear', /\b(best|top|popular|trending|hot)\b/i.test(t) ? 'I don’t have sales rankings to share, but here’s our hand-picked featured gear from across the store.' : 'Hand-picked gear from across our departments.');
    if (ctx.product && /\b(this (item|one|product)|in stock|sizes?|colors?|available)\b/i.test(t) && !/\b(show|find|other|another)\b/i.test(t)) return productHereFlow('stock');
    if (/\b(what size|which size|size chart|sizing|will (this|it) fit|does (this|it) fit|fit me|run (small|large|big|true)|compatible|compatibility|works with)\b/i.test(t)) {
      say('I won’t guess on fit or compatibility. Check the size chart and specs on the product page, and if you’re between sizes or unsure, ask the team with your measurements or equipment model.', 'Fit & compatibility', { links: [{ label: 'Ask the team', href: R.contact }] });
      if (ctx.product) return productHereFlow('stock');
      return;
    }
    if (/\b(ballistic|body armor|plate carrier|helmet|ifak|tourniquet|respirator|gas mask|medical)\b/i.test(t)) say('I’ll find these, but I won’t state protection levels, medical performance, or certifications — verify those on each product page.', 'Safety note');
    if (/\b(gun|firearm|holster|ammunition|ammo|knife|knives|weapon|optic|scope|night vision|thermal)\b/i.test(t)) say('I can find what the store sells and compare listings. Laws on purchase, possession, carry, and export are the buyer’s responsibility.', 'Heads up');
    return searchFlow(t);
  }

  // ---------- events ----------
  function onOpen() {
    const chips = contextChips();
    if (chips.length && !panel.dataset.ctxShown) {
      panel.dataset.ctxShown = '1';
      const last = state.log[state.log.length - 1];
      const label = ctx.product ? `Looking at ${ctx.product.title}? I can help with this item.` : `Browsing ${ctx.collection.title}? I can pull in-stock picks from here.`;
      if (!(last && last.text === label)) say(label, '', { chips });
    }
    cartFlow(true);
  }
  openBtn.addEventListener('click', () => show(panel.hidden));
  closeBtn && closeBtn.addEventListener('click', () => show(false));
  clearBtn && clearBtn.addEventListener('click', () => {
    state.log = []; state.last = []; state.branch = ''; state.budget = null; state.lastQuery = ''; save();
    messages.querySelectorAll(':scope > :not([data-qm-welcome])').forEach((n) => n.remove());
    panel.dataset.ctxShown = ''; say('Fresh start. What can I help you find?', '', { chips: contextChips() });
  });
  const ACTIONS = {
    find: () => { say('Tell me the item or category. Add branch, size, color or camo, brand, or budget if they matter — for example “Army OCP boonie under $40”.'); input.focus(); },
    branch: branchMenu, order: orderFlow, custom: () => customFlow(''), bulk: bulkFlow, cart: () => cartFlow(false), compare: compareFlow,
    new: () => collectionFlow(R.newCol, 'New arrivals', 'The newest listings in the store, newest first.'),
    featured: () => collectionFlow(R.featCol, 'Featured gear', 'Hand-picked gear from across our departments.'),
    uniforms: () => handle('uniforms insignia', true), outdoor: () => handle('outdoor survival preparedness', true), medical: () => handle('first aid IFAK', true),
    setbranch: (arg) => { state.branch = arg; save(); say(`Got it — I’ll prioritize ${arg} items. What do you need?`, '', { chips: [{ label: `${arg} apparel`, q: `${arg} shirt` }, { label: `${arg} patches`, q: `${arg} patch` }, { label: `${arg} gifts`, q: `${arg} gift` }] }); },
    clearbudget: (arg) => { state.budget = null; save(); if (arg) handle(arg, false); },
    'here-stock': () => productHereFlow('stock'), 'here-pair': () => productHereFlow('pair'),
    'here-col': () => ctx.collection && collectionFlow(ctx.collection.handle, `Picks from ${ctx.collection.title}`, `A few in-stock items from ${ctx.collection.title}.`)
  };
  const runAction = (a, arg) => { if (state.busy) return; const f = ACTIONS[a]; if (f) f(arg); };
  quick && quick.addEventListener('click', (e) => { const b = e.target.closest('[data-action]'); if (b) runAction(b.dataset.action); });
  messages.addEventListener('click', (e) => {
    const add = e.target.closest('[data-qm-add]'); if (add) { e.preventDefault(); return addToCart(add); }
    const b = e.target.closest('button[data-query],button[data-act]'); if (!b) return;
    if (b.dataset.act) return runAction(b.dataset.act, b.dataset.arg);
    handle(b.dataset.query || b.textContent || '');
  });
  form.addEventListener('submit', (e) => { e.preventDefault(); const t = input.value; input.value = ''; input.style.height = ''; handle(t); });
  input.addEventListener('input', () => { input.style.height = 'auto'; input.style.height = `${Math.min(input.scrollHeight, 116)}px`; });
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })); } });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) show(false); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) getCart().catch(() => {}); });

  // ---------- boot ----------
  load();
  state.log.forEach((en) => push(en, false));
  if (state.open) show(true, false);
  getCart().catch(() => {});
})();
